import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
    fetchMyNotifications,
    NOTIFICATIONS_API_LIVE,
} from "../../api/pushServices";

// There is no read/unread state here, deliberately.
//
// The API has no read column: a notification row is the same row for everyone
// it reaches, and a broadcast is a single shared row. "Read" was tracked
// locally in localStorage for a while, which meant it was per-device — the same
// notification could be read on a phone and unread on a laptop, and clearing
// site data marked everything unread again. That is a worse answer than not
// answering, so the whole idea waits for the backend to carry it.
//
// If a read flag does arrive, it belongs in the fetched row, not in a parallel
// local structure keyed on NotificationId.

const initialState = {
    // Newest first, as returned by the API.
    items: [],
    count: 0,
    isLoading: false,
    error: null,
};

// Reads the signed-in user's inbox: their targeted notifications plus every
// broadcast. POST despite being a read — that is the API's shape.
//
// `take` defaults to 50 server-side and is capped at 200.
// A 500 from this controller currently returns a verbose diagnostic body —
// `exceptionMessage`, `stack`, and friends — to help during the Firebase
// rollout. Only `message` is read here, and even that is never rendered: the
// panel shows its own generic copy and keeps this string for logging. Those
// diagnostic fields are explicitly temporary, so nothing may branch on them.
export const fetchNotifications = createAsyncThunk(
    "notifications/fetchMine",
    ({ take } = {}, { rejectWithValue }) =>
        fetchMyNotifications({ take }).catch((error) =>
            rejectWithValue(
                error?.response?.data?.message || error?.message || "Request failed"
            )
        )
);

export const notificationsSlice = createSlice({
    name: "notificationsSlice",
    initialState,
    reducers: {
        clearNotificationsError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchNotifications.pending, (state) => {
            // Only show the spinner on a cold load; a background refresh after a
            // push should not blank out a list the user is already reading.
            if (!state.items.length) {
                state.isLoading = true;
            }
        });
        builder.addCase(fetchNotifications.fulfilled, (state, action) => {
            state.isLoading = false;
            state.error = null;
            state.items = action.payload?.notifications || [];
            state.count = action.payload?.count ?? state.items.length;
        });
        builder.addCase(fetchNotifications.rejected, (state, action) => {
            state.isLoading = false;
            const reason = action.payload || action.error?.message;
            // Until the backend is live this is not news, and there is nothing
            // the customer can do with it: the panel reads a null error as an
            // empty inbox and shows its ordinary empty state instead of an
            // error box on every open. The reason still reaches the console.
            // See NOTIFICATIONS_API_LIVE in api/pushServices.
            if (!NOTIFICATIONS_API_LIVE) {
                console.warn("[Notifications] inbox read failed:", reason);
                state.error = null;
                return;
            }
            state.error = reason;
        });
    },
});

// SELECTORS
export const selectNotificationsDatatWhole = (store) => store?.notifications;
export const selectNotificationsData = (store) =>
    store?.notifications?.items || [];
export const selectNotificationsCount = (store) =>
    store?.notifications?.count || 0;
export const selectNotificationsDataLoader = (store) =>
    store?.notifications?.isLoading;
export const selectNotificationsError = (store) => store?.notifications?.error;

// ACTIONS TO DISPATCH
export const { clearNotificationsError } = notificationsSlice.actions;

export default notificationsSlice.reducer;
